import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import Price from '../Price';
import { ApiError, ItemSearchHit, searchItems } from '../../../api';
import { useAuth } from '../../../auth/AuthContext';

// Tenant slug → short human label for the row chip. Mirrors the
// ITEM_TENANTS table on the server. Hardcoded for now — there are two
// live tenants and they rarely change.
const TENANT_LABELS: Record<string, string> = {
  backroom: 'Back Room',
  ags: 'Grow Shop',
};

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (hit: ItemSearchHit) => void;
  /** When provided and the "Match price" toggle is on, restricts hits
   *  to items priced exactly at this value (integer cents). */
  priceHint?: number | null;
};

const DEBOUNCE_MS = 300;

const ItemPickerDialog: React.FC<Props> = ({
  open,
  onClose,
  onPick,
  priceHint,
}) => {
  const { idToken } = useAuth();
  const [query, setQuery] = useState('');
  const [matchPrice, setMatchPrice] = useState<boolean>(
    !!priceHint && priceHint > 0,
  );
  const [hits, setHits] = useState<ItemSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedIdx, setSelectedIdx] = useState<number>(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset transient state every time the dialog opens — picking up a
  // stale query/selection from the previous line item would be a
  // footgun in a multi-row reconcile session.
  useEffect(() => {
    if (open) {
      setQuery('');
      setHits([]);
      setError('');
      setSelectedIdx(-1);
      setMatchPrice(!!priceHint && priceHint > 0);
    }
  }, [open, priceHint]);

  useEffect(() => {
    if (!open) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = query.trim();
    if (!trimmed) {
      setHits([]);
      setLoading(false);
      setError('');
      return;
    }
    if (!idToken) return;
    setLoading(true);
    setError('');
    timerRef.current = setTimeout(async () => {
      try {
        const results = await searchItems(idToken, trimmed, {
          priceCents:
            matchPrice && priceHint && priceHint > 0 ? priceHint : undefined,
          limit: 25,
        });
        setHits(results);
        setSelectedIdx(-1);
      } catch (e) {
        setError(
          e instanceof ApiError
            ? `API error ${e.status}`
            : (e as Error)?.message || 'search failed',
        );
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, query, matchPrice, priceHint, idToken]);

  const handleOk = () => {
    if (selectedIdx < 0 || selectedIdx >= hits.length) return;
    onPick(hits[selectedIdx]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reconcile against catalog</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Pick the catalog item this line corresponds to. Records the
          item id and tenant on the reconciliation so the auto-sweep
          can decrement stock on the next pass.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <TextField
            label="Search name or brand"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          {priceHint != null && priceHint > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={matchPrice}
                  onChange={(e) => setMatchPrice(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  Match <Price price={priceHint} showZero />
                </Typography>
              }
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 240, position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && query.trim() === '' && (
            <Typography
              variant="body2"
              sx={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', mt: 4 }}
            >
              Type to search the catalog.
            </Typography>
          )}
          {!loading && query.trim() !== '' && hits.length === 0 && !error && (
            <Typography
              variant="body2"
              sx={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', mt: 4 }}
            >
              No matches.
            </Typography>
          )}
          <List dense disablePadding>
            {hits.map((hit, idx) => (
              <ListItemButton
                key={`${hit.tenant}-${hit.id}`}
                selected={selectedIdx === idx}
                onClick={() => setSelectedIdx(idx)}
                onDoubleClick={() => onPick(hit)}
                sx={{ alignItems: 'flex-start' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {hit.brand ? `${hit.brand} — ` : ''}
                    {hit.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: '#94a3b8', fontFamily: 'monospace' }}
                  >
                    {hit.id}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    ml: 1,
                  }}
                >
                  <Chip
                    label={TENANT_LABELS[hit.tenant] ?? hit.tenant}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    <Price price={hit.price ?? 0} showZero />
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleOk}
          variant="contained"
          disabled={selectedIdx < 0}
          sx={{ textTransform: 'none' }}
        >
          Reconcile
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemPickerDialog;
